// Why an order has left the normal pack -> label -> handoff path, and where it
// is resolved. Presentation only: the server decides every state named here,
// and every action below is an existing page with its own server permission.
//
// kind:
//   action  - someone must decide or do something, and `actions` says where
//   waiting - nothing to do yet; the system or the shopper acts next
//   settled - the outcome is final for delivery; shown as a fact, not as work

const PARCEL = (order) => order?.shipmentId
  ? { to: `/business/delivery?view=parcels&parcel=${order.shipmentId}`, label: 'Open the parcel', permissions: ['delivery.dispatch.read.business'] }
  : null;
const RIDER_RETURNS = (permissions, label = 'Decide in Rider returns') => ({
  to: '/business/delivery?view=returns', label, permissions, primary: true,
});
const SETTLEMENT = { to: '/business/delivery/settlement', label: 'Open the Settlement desk', permissions: ['delivery.cod.remit.business'] };

const RETURNS_PERMISSIONS = ['delivery.custody.receive.business', 'delivery.returns.disposition.business'];
// The manager's decision on an escalated return is its own, narrower authority.
const MANAGER_PERMISSIONS = ['delivery.exceptions.correct.business'];

const compact = (list) => list.filter(Boolean);

function holdAttention(order) {
  const hold = order.dispatchHold;
  const code = String(hold.code || '');
  const closed = String(order.fulfillmentStatus || '').toUpperCase() === 'CANCELLED';

  if (hold.next === 'RETURNS_DESK') {
    return {
      kind: 'action', title: hold.title, message: hold.message,
      actions: compact([RIDER_RETURNS(RETURNS_PERMISSIONS), PARCEL(order)]),
      unavailable: 'Someone who handles rider returns needs to decide this.',
    };
  }
  if (hold.next === 'SETTLEMENT_DESK') {
    return {
      kind: 'action', title: hold.title, message: hold.message,
      actions: compact([{ ...SETTLEMENT, primary: true }, PARCEL(order)]),
      unavailable: 'Someone who handles rider cash needs to look into this at the Settlement desk.',
    };
  }
  if (code === 'RETURN_MANAGEMENT_REVIEW') {
    return {
      kind: 'action', title: hold.title,
      message: 'It came back from delivery and was passed to a manager. The manager decides in Rider returns whether it goes out again, back into stock, or is written off.',
      actions: compact([RIDER_RETURNS(MANAGER_PERMISSIONS, 'Decide in Rider returns'), PARCEL(order)]),
      unavailable: 'A manager needs to decide this in Rider returns.',
    };
  }
  if (code === 'RETURN_RESTOCKED' || code === 'RETURN_WRITTEN_OFF') {
    return {
      kind: 'settled', title: hold.title, message: hold.message,
      // A decided return normally closes its order. If this one is still open,
      // say so rather than leaving the reader to wonder what to pack.
      note: closed ? null : 'This order still shows as open, which is unexpected after that decision. There is no fulfillment work left to do on it.',
      actions: compact([PARCEL(order)]),
    };
  }
  // Anything else is a pickup hold someone placed by hand. Its "code" is the
  // reason they typed, so it is shown as a quote unless it looks like a code.
  const typedReason = /^[A-Z0-9_]+$/.test(code) ? null : code;
  return {
    kind: 'action', title: typedReason ? 'Pickup is on hold' : 'This parcel is on hold',
    message: typedReason
      ? 'Someone put this parcel on hold, so it will not be handed to a rider. Clear the hold on the parcel once it is physically ready.'
      : 'It will not be sent out until the hold is cleared. Open the parcel to see its current state.',
    reason: typedReason,
    actions: compact([PARCEL(order) && { ...PARCEL(order), label: 'Open the parcel to clear the hold', primary: true }]),
    unavailable: 'Someone who can release pickups needs to clear this hold on the parcel.',
  };
}

export function orderAttention(order) {
  if (!order) return null;
  if (order.dispatchHold) return { blocksWorkflow: true, ...holdAttention(order) };
  if (String(order.shipmentStatus || '').toUpperCase() === 'FAILED') {
    return {
      blocksWorkflow: true, kind: 'settled',
      title: 'Delivery of this parcel failed',
      message: 'It is no longer in dispatch and will not be sent out again. Any cash still owed for it is followed up at the Settlement desk.',
      actions: compact([SETTLEMENT, PARCEL(order)]),
    };
  }
  if (order.nextAction === 'AWAIT_PAYMENT') {
    return {
      blocksWorkflow: true, kind: 'waiting',
      title: "Waiting for the shopper's payment",
      message: 'This order was placed with online payment and the payment has not settled. Packing opens by itself once it does. There is nothing to do yet.',
      actions: [],
    };
  }
  return null;
}

// True when the order is waiting on a person, as opposed to waiting on the
// system or already settled. Orders blocked inside a normal step (a failed
// readiness check, a dispatch that found no rider) also count: that step shows
// them what to fix.
export function needsDecision(order) {
  const attention = orderAttention(order);
  return !attention || attention.kind === 'action';
}

export function allowedActions(attention, can) {
  if (!attention) return [];
  return attention.actions.filter((action) => !action.permissions || action.permissions.some(can));
}

// An order still has fulfillment work while it is open, not held, and its
// parcel has not left for good. The server decides the stage; this only
// decides whether to offer the way into Fulfillment.
export function hasFulfillmentWork(order = {}) {
  const fulfillment = String(order.fulfillmentStatus || '').toUpperCase();
  const shipment = String(order.shipmentStatus || '').toUpperCase();
  return ['PENDING', 'PROCESSING', 'PACKED', 'READY_FOR_DISPATCH'].includes(fulfillment)
    && !['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED', 'FAILED'].includes(shipment)
    && order.delivery?.assignmentStatus !== 'PICKED_UP'
    && !order.dispatchHold;
}
